/**
 * MemberLegalCenter — los documentos legales dentro de la cuenta del comerciante.
 *
 * ⚠️ Esta pantalla afirmaba una aceptacion que nunca ocurrio. Tenia una franja
 * verde que decia, textual: "You agreed to all documents below at signup."
 *
 * Medido el 27-set: **no existia ninguna aceptacion.** `UserCreate` pide correo,
 * contrasena y nombre de empresa, nada mas. La pantalla de registro no tiene
 * casilla de terminos, y no hay coleccion donde se guarde nada de eso.
 *
 * Es el mismo patron que se apago toda la semana, y estaba en **la pantalla que
 * el comerciante mira si alguna vez discute**: le decia "vos aceptaste esto" sin
 * poder mostrar cuando, que version, ni desde donde.
 *
 * Ahora muestra el estado real y deja aceptar de verdad, contra
 * `/api/member-terms`, que registra hora, IP, agente y un hash de evidencia.
 *
 * Routes:
 *   /dashboard/legal              → index
 *   /dashboard/legal/:slug        → specific doc
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Shield, Scale, Landmark, Lock, Truck, Eye, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { LEGAL_DOCS, LEGAL_BY_SLUG, EFFECTIVE_DATE, COMPANY } from '@/legal/legalContent';
import LegalDocument from '@/legal/LegalDocument';

const ICON_BY_SLUG = {
  'terms':                          FileText,
  'privacy':                        Eye,
  'shipping-terms':                 Truck,
  'restricted-prohibited-products': AlertTriangle,
  'aml-compliance':                 Scale,
  'regulatory':                     Landmark,
  'non-custodial':                  Lock,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberLegalCenter() {
  const { slug } = useParams();
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [marcada, setMarcada] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API}/member-terms`, { headers: auth });
      if (r.ok) setEstado(await r.json());
    } catch (e) {
      setError('Could not read your acceptance record.');
    } finally {
      setCargando(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!slug) cargar(); }, [slug, cargar]);

  const aceptar = async () => {
    setEnviando(true); setError(null);
    try {
      const r = await fetch(`${API}/member-terms`, {
        method: 'POST', headers: auth,
        body: JSON.stringify({ effective_date: EFFECTIVE_DATE }),
      });
      const d = await r.json();
      if (!r.ok) {
        // 409 significa que los documentos cambiaron mientras la pantalla
        // estaba abierta. Nadie queda atado a una version que no vio.
        setError(typeof d.detail === 'string' ? d.detail
                 : (d.detail?.message || 'Could not record your acceptance.'));
        await cargar();
        return;
      }
      setEstado(d);
    } catch (e) {
      setError('The call failed. Nothing was recorded.');
    } finally {
      setEnviando(false);
    }
  };

  if (slug) {
    const doc = LEGAL_BY_SLUG[slug];
    return <LegalDocument doc={doc} backTo="/dashboard/legal" backLabel="Legal Center" embedded />;
  }

  return (
    <div className="max-w-4xl mx-auto px-5 py-8">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-emerald-600 mb-2">
          Members · Legal Center · <span className="text-amber-600">Full binding version</span>
        </div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Shield className="w-5 h-5" /> Your terms & policies
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          These are the <strong>complete, legally-binding</strong> documents, not the public
          summaries. In force since {EFFECTIVE_DATE}.
          Contact <a href={`mailto:${COMPANY.legalEmail}`} className="text-emerald-600 hover:underline">{COMPANY.legalEmail}</a>{' '}
          with any questions.
        </p>
      </div>

      {/* Estado real de la aceptacion. Antes esta franja afirmaba que el
          comerciante habia aceptado al registrarse, y eso nunca ocurrio. */}
      {cargando && <p className="text-sm text-slate-500 mb-5">Reading your record...</p>}

      {!cargando && estado?.accepted && (
        <Card className="mb-5 border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/20 dark:border-emerald-900"
              data-testid="member-terms-accepted">
          <CardContent className="p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-emerald-800 dark:text-emerald-200">
                You accepted the documents in force on {estado.effective_date}.
              </p>
              <p className="text-slate-600 dark:text-slate-300 mt-1">
                Recorded {new Date(estado.accepted_at).toLocaleString()}.
              </p>
              <p className="text-xs text-slate-500 font-mono break-all mt-1">
                evidence {estado.evidence_hash}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                The hash covers your account, the version, the time and where the
                acceptance came from. If any of those changed afterwards, it would no
                longer match. Print or PDF any document from its own page.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!cargando && estado && !estado.accepted && (
        <Card className="mb-5 border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900"
              data-testid="member-terms-pending">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-900 dark:text-amber-200">
                  No acceptance is on record.
                </p>
                <p className="text-amber-800 dark:text-amber-300 mt-1">
                  Nothing was recorded when this account was created. This page used to
                  say you had agreed at signup, and that was not accurate. Read the
                  documents below and accept them here, and the record will show the
                  version, the time and a hash that ties them together.
                </p>
              </div>
            </div>

            {error && (
              <p className="text-sm text-rose-700 dark:text-rose-300 mt-3" data-testid="member-terms-error">
                {error}
              </p>
            )}

            <label className="flex items-start gap-2 text-sm cursor-pointer mt-4">
              <input type="checkbox" className="mt-1" checked={marcada}
                     onChange={(e) => setMarcada(e.target.checked)}
                     data-testid="member-terms-check" />
              <span>
                I have read the documents below and I accept them on behalf of my
                business.
              </span>
            </label>

            <Button className="mt-3" onClick={aceptar} disabled={!marcada || enviando}
                    data-testid="member-terms-accept">
              {enviando
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Recording</>
                : 'Accept these documents'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Doc list */}
      <div className="space-y-2">
        {LEGAL_DOCS.map((doc) => {
          const Icon = ICON_BY_SLUG[doc.slug] || FileText;
          return (
            <Link key={doc.slug} to={`/dashboard/legal/${doc.slug}`} data-testid={`member-legal-${doc.slug}`}>
              <Card className="hover:border-emerald-400 transition cursor-pointer">
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold">{doc.title}</div>
                      <Badge variant="outline" className="text-[10px]">v1.0</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">{doc.intro?.slice(0, 160)}…</div>
                  </div>
                  <div className="text-xs text-emerald-600 shrink-0 pt-2">Open →</div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Public copies notice */}
      <div className="mt-6 text-[11px] text-slate-500 text-center">
        Public copies are always available at{' '}
        <Link to="/legal" className="text-emerald-600 hover:underline">{COMPANY.website.replace('https://','')}/legal</Link>.
      </div>
    </div>
  );
}
